/**
 * Miscellaneous additions — brewing salts, alkalis, acids, finings, nutrients,
 * water treatment, chelators, enzymes, processing aids, spices and hop-flavour
 * products.
 *
 * This file is identity, concentration and dosing guidance only. The ion
 * contributions of a salt and the acid equivalence maths that turn a
 * `concentrationPercent` into a predicted mash pH deliberately live in
 * `@beerwright/core` (PLAN §5.4/§5.5), so that the catalog stays a flat data
 * product and the chemistry has exactly one home.
 *
 * `typicalDosage` is written in words on purpose: doses here are starting
 * points a brewer reads and judges, never numbers a calculator consumes.
 */

import type { Misc } from '../../schema/misc';

/** Miscellaneous brewing additions. @see Misc */
export const miscAdditions: readonly Misc[] = [
  // ---------------------------------------------------------------------------
  // Water salts — mineral additions made to the brewing liquor
  // ---------------------------------------------------------------------------
  {
    id: 'gypsum',
    name: 'Gypsum (Calcium Sulfate)',
    type: 'water-agent',
    use: 'mash',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Stirred into the strike and sparge water before the grain goes in.',
    concentrationPercent: null,
    chemicalFormula: 'CaSO₄·2H₂O',
    typicalDosage: '0.2–1 g/L of brewing water',
    dosePerLiter: null,
    description:
      'The workhorse salt for hop-forward beer: it supplies calcium for mash pH and yeast health, and sulfate to sharpen bitterness into something drier and more assertive. Burton-style pale ales lean on it heavily.',
    note: 'Dissolves slowly in cold water — stir it in early or add it to the mash directly. Past roughly 300 ppm sulfate the bitterness turns thin and minerally rather than crisp.',
    tags: ['water-agent', 'calcium', 'sulfate', 'hoppy', 'burton', 'salt'],
    provenance: 'spec-sheet',
  },
  {
    id: 'calcium-chloride-dihydrate',
    name: 'Calcium Chloride (Dihydrate)',
    type: 'water-agent',
    use: 'mash',
    form: 'granular',
    timingMinutes: null,
    timingNote: 'Dissolved into the brewing water ahead of the mash.',
    concentrationPercent: null,
    chemicalFormula: 'CaCl₂·2H₂O',
    typicalDosage: '0.2–1 g/L of brewing water',
    dosePerLiter: null,
    description:
      'Adds calcium without sulfate, and chloride to round the palate out — beers taste fuller and maltier rather than drier. The standard choice for bitters, stouts and anything where hop bite should stay soft.',
    note: 'This is the common homebrew form and the one most water calculators assume. It is badly hygroscopic: a jar left open turns to slush and the effective dose drifts, so keep it sealed and weigh it fresh.',
    tags: ['water-agent', 'calcium', 'chloride', 'malty', 'salt', 'dihydrate'],
    provenance: 'spec-sheet',
  },
  {
    id: 'calcium-chloride-anhydrous',
    name: 'Calcium Chloride (Anhydrous)',
    type: 'water-agent',
    use: 'mash',
    form: 'granular',
    timingMinutes: null,
    timingNote: 'Dissolved into the brewing water ahead of the mash.',
    concentrationPercent: null,
    chemicalFormula: 'CaCl₂',
    typicalDosage: '0.15–0.75 g/L of brewing water',
    dosePerLiter: null,
    description:
      'The water-free form of calcium chloride, sold as hard prills or flake. Same job as the dihydrate — calcium plus a fuller, maltier chloride character — but a given weight carries considerably more of it.',
    note: 'Carries roughly a third more calcium and chloride per gram than the dihydrate, so a recipe written for one form is materially wrong for the other. Confirm which you have before dosing, and store it sealed — it pulls water from the air and slowly becomes the dihydrate.',
    tags: ['water-agent', 'calcium', 'chloride', 'malty', 'salt', 'anhydrous'],
    provenance: 'spec-sheet',
  },
  {
    id: 'epsom-salt',
    name: 'Epsom Salt (Magnesium Sulfate)',
    type: 'water-agent',
    use: 'mash',
    form: 'granular',
    timingMinutes: null,
    timingNote: 'Dissolved into the brewing water ahead of the mash.',
    concentrationPercent: null,
    chemicalFormula: 'MgSO₄·7H₂O',
    typicalDosage: '0.05–0.3 g/L of brewing water',
    dosePerLiter: null,
    description:
      'Supplies magnesium and sulfate together. A small dose adds a little yeast nutrition and a dry, brisk edge to bitterness; it is a supporting salt, not a base for a whole water profile.',
    note: 'Magnesium above about 30 ppm tastes sour and metallic and is mildly laxative. Most malt bills already supply all the magnesium the yeast needs, so use gypsum for sulfate and keep this addition small.',
    tags: ['water-agent', 'magnesium', 'sulfate', 'salt'],
    provenance: 'spec-sheet',
  },
  {
    id: 'magnesium-chloride',
    name: 'Magnesium Chloride (Hexahydrate)',
    type: 'water-agent',
    use: 'mash',
    form: 'granular',
    timingMinutes: null,
    timingNote: 'Dissolved into the brewing water ahead of the mash.',
    concentrationPercent: null,
    chemicalFormula: 'MgCl₂·6H₂O',
    typicalDosage: '0.05–0.3 g/L of brewing water',
    dosePerLiter: null,
    description:
      'The chloride route to magnesium, useful when a profile needs a touch more magnesium but no extra sulfate. Reads as a subtle fullness rather than the dry snap Epsom salt gives.',
    note: 'Extremely hygroscopic — the crystals clump and weep in humid air, which makes weighing unreliable unless the tub is kept sealed. Same magnesium ceiling applies: stay under about 30 ppm.',
    tags: ['water-agent', 'magnesium', 'chloride', 'salt', 'hexahydrate'],
    provenance: 'spec-sheet',
  },
  {
    id: 'sinamar',
    name: 'Sinamar (Roasted Malt Extract)',
    type: 'other',
    use: 'boil',
    form: 'liquid',
    timingMinutes: null,
    timingNote:
      'Anywhere from the kettle to the bottling line — it is color, not flavor, so the timing barely matters.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '14 mL/hL raises color by roughly 1 EBC',
    dosePerLiter: null,
    description:
      "Weyermann's liquid roasted-malt extract, vacuum-concentrated from Carafa Special to 8100-8600 EBC. It is here rather than among the fermentables because it is not a malt: it carries no fermentable sugar, no alcohol and no diastatic power, and it is dosed by volume rather than weighed into a grist. What it buys is color without roast flavor — a schwarzbier that stays smooth, or a dunkel corrected at the end of the boil instead of re-designed at the start of it.",
    note: 'Dose is per EBC unit rather than a fixed rate, so it scales with how far the color has to move. Being Röstmalzbier it is a beer ingredient under the Reinheitsgebot, which is why German breweries can use it where a caramel coloring would be refused.',
    tags: ['colour', 'roasted', 'extract', 'weyermann', 'liquid', 'schwarzbier'],
    provenance: 'spec-sheet',
  },
  {
    id: 'table-salt',
    name: 'Table Salt (Sodium Chloride, Non-Iodised)',
    type: 'water-agent',
    use: 'mash',
    form: 'granular',
    timingMinutes: null,
    timingNote: 'Dissolved into the brewing water ahead of the mash.',
    concentrationPercent: null,
    chemicalFormula: 'NaCl',
    typicalDosage: '0.05–0.4 g/L of brewing water',
    dosePerLiter: null,
    description:
      'Sodium plus chloride. In modest amounts it thickens body and accentuates malt sweetness, which is why it turns up in milds, sweet stouts and, in much larger doses, gose.',
    note: 'Must be non-iodized and free of anti-caking agents — iodine harms yeast and can throw medicinal off-flavors. Sodium above roughly 150 ppm starts tasting frankly salty, and the combination of high sodium with high sulfate is harsh.',
    tags: ['water-agent', 'sodium', 'chloride', 'salt', 'gose'],
    provenance: 'spec-sheet',
  },

  // ---------------------------------------------------------------------------
  // Alkaline agents — raising mash pH and alkalinity
  // ---------------------------------------------------------------------------
  {
    id: 'baking-soda',
    name: 'Baking Soda (Sodium Bicarbonate)',
    type: 'alkali',
    use: 'mash',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Added to the mash water, or stirred into the mash to correct a low pH.',
    concentrationPercent: null,
    chemicalFormula: 'NaHCO₃',
    typicalDosage: '0.1–0.5 g/L of mash water',
    dosePerLiter: null,
    description:
      'The reliable way to raise mash pH when a dark, acidic grist drives it too low. Dissolves readily and acts immediately, unlike chalk.',
    note: 'Every gram of alkalinity comes with sodium attached, so heavy doses salt the beer before they fix the pH. Reserve it for dark beers on soft water and keep total sodium under about 150 ppm.',
    tags: ['alkali', 'bicarbonate', 'sodium', 'ph', 'dark-beer'],
    provenance: 'spec-sheet',
  },
  {
    id: 'chalk',
    name: 'Chalk (Calcium Carbonate)',
    type: 'alkali',
    use: 'mash',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Traditionally stirred into the mash; predose the water only if it is carbonated.',
    concentrationPercent: null,
    chemicalFormula: 'CaCO₃',
    typicalDosage: '0.5–2 g/L of mash water, with low confidence',
    dosePerLiter: null,
    description:
      'The classic textbook alkali for dark beers, offering calcium and carbonate together. In practice it is the least dependable pH tool in the cupboard and is best treated as a last resort.',
    note: 'Chalk is barely soluble in mash-strength water — much of what you weigh out sits in the tun as grit and never reacts, so the real pH shift is often a fraction of what a calculator predicts. Prefer baking soda or slaked lime, or dissolve chalk under CO₂ pressure first.',
    tags: ['alkali', 'carbonate', 'calcium', 'ph', 'insoluble', 'dark-beer'],
    provenance: 'spec-sheet',
  },
  {
    id: 'slaked-lime',
    name: 'Slaked Lime (Pickling Lime)',
    type: 'alkali',
    use: 'water',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Added to the brewing water, or dosed into the mash in tiny increments.',
    concentrationPercent: null,
    chemicalFormula: 'Ca(OH)₂',
    typicalDosage: '0.05–0.2 g/L of brewing water',
    dosePerLiter: null,
    description:
      'A strong alkali that raises pH while adding calcium and no sodium at all, which makes it the cleanest correction for a dark grist on soft water. Also used to decarbonate hard water by precipitating out bicarbonate.',
    note: 'Far more potent per gram than baking soda — a careless scoop will overshoot badly, so weigh small and adjust. It is caustic: avoid breathing the dust and keep it out of the eyes.',
    tags: ['alkali', 'hydroxide', 'calcium', 'ph', 'decarbonation'],
    provenance: 'spec-sheet',
  },
  {
    id: 'sodium-carbonate',
    name: 'Sodium Carbonate (Washing Soda)',
    type: 'alkali',
    use: 'water',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Dissolved into the brewing water before the mash.',
    concentrationPercent: null,
    chemicalFormula: 'Na₂CO₃',
    typicalDosage: '0.05–0.3 g/L of brewing water',
    dosePerLiter: null,
    description:
      'A stronger, more soluble relative of baking soda that raises pH quickly. Useful when a small volume of alkalinity is wanted without the bulk of bicarbonate.',
    note: 'Roughly twice the pH punch of baking soda per gram and it still carries sodium, so it overshoots easily. Use the food-grade product, not the laundry aisle version with fragrance or brighteners in it.',
    tags: ['alkali', 'carbonate', 'sodium', 'ph'],
    provenance: 'spec-sheet',
  },
  {
    id: 'potassium-bicarbonate',
    name: 'Potassium Bicarbonate',
    type: 'alkali',
    use: 'mash',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Dissolved into the mash water, or added to the mash to lift a low pH.',
    concentrationPercent: null,
    chemicalFormula: 'KHCO₃',
    typicalDosage: '0.1–0.6 g/L of mash water',
    dosePerLiter: null,
    description:
      'Raises pH exactly as baking soda does but leaves potassium behind instead of sodium, which is flavor-neutral at brewing levels. The better choice when a beer already carries as much sodium as it can take.',
    note: 'Very hygroscopic and prone to caking — keep it sealed and break up lumps before weighing. Potassium is well tolerated by yeast but very large doses can read faintly salty.',
    tags: ['alkali', 'bicarbonate', 'potassium', 'ph'],
    provenance: 'spec-sheet',
  },

  // ---------------------------------------------------------------------------
  // Finings — kettle and cold-side clarification
  // ---------------------------------------------------------------------------
  {
    id: 'irish-moss',
    name: 'Irish Moss',
    type: 'fining',
    use: 'boil',
    form: 'granular',
    timingMinutes: 15,
    timingNote: 'Added roughly 15 minutes before knockout so it has time to rehydrate and act.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1 g per 20 L of wort',
    dosePerLiter: 0.05,
    description:
      'Dried red seaweed whose carrageenan grabs hot-break protein and drags it into a tight cold break. The traditional kettle fining, and still the cheapest way to get bright wort into the fermenter.',
    note: 'Rehydrate it in a little water for 15 minutes before use — dry flakes tossed into the kettle largely float and do nothing. Overdosing can strip so much protein that head retention suffers.',
    tags: ['fining', 'kettle-fining', 'carrageenan', 'seaweed', 'clarity'],
    provenance: 'representative',
  },
  {
    id: 'whirlfloc-tablet',
    name: 'Whirlfloc Tablet',
    type: 'fining',
    use: 'boil',
    form: 'tablet',
    timingMinutes: 5,
    timingNote: 'Dropped into the kettle about 5 minutes before knockout.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: 'Half a tablet per 19 L (5 gal) of wort, one per 38 L (10 gal)',
    dosePerLiter: 0.025,
    description:
      'A compressed blend of purified carrageenan and polyvinylpolypyrrolidone that does the Irish moss job faster and more consistently. No rehydration, no weighing — just drop a tablet in near the end of the boil.',
    note: 'Half a tablet clears a 5 gal batch; a whole one is for 10 gal, and doubling it only compacts the trub less. Add it too early and the boil shears the flocs apart, undoing the work.',
    tags: ['fining', 'kettle-fining', 'carrageenan', 'tablet', 'clarity'],
    provenance: 'representative',
  },
  {
    id: 'gelatin',
    name: 'Gelatin (Unflavoured)',
    type: 'fining',
    use: 'secondary',
    form: 'powder',
    timingMinutes: 2880,
    timingNote: 'Left in contact for about 48 hours at cold-crash temperature before packaging.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1–2 g per 20 L of beer',
    dosePerLiter: null,
    description:
      'A positively charged protein that pulls yeast and haze particles into a firm compact layer on the bottom. The fastest and cheapest route from murky to brilliant on the cold side.',
    note: 'Bloom it in about 60 mL of water, then hold at 60–70 °C to dissolve — boiling destroys it and undissolved granules do nothing. It only works on beer that has been cold-crashed to a few degrees above freezing, and it is not vegetarian.',
    tags: ['fining', 'cold-side', 'gelatin', 'clarity', 'cold-crash'],
    provenance: 'representative',
  },
  {
    id: 'biofine-clear',
    name: 'Biofine Clear',
    type: 'fining',
    use: 'secondary',
    form: 'liquid',
    timingMinutes: 2880,
    timingNote: 'Stirred in gently and left about 48 hours before racking or packaging.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '20–40 mL per 20 L of beer',
    dosePerLiter: 1.5,
    description:
      'A ready-to-use silicic acid sol that drops yeast and protein haze out in a day or two without heating or blooming. Vegan, works at cellar as well as crash temperatures, and leaves an unusually compact sediment.',
    note: 'Dose into the beer, not the other way round, and mix with the gentlest possible stir — vigorous agitation both oxidizes the beer and breaks the flocs. Shake the bottle before measuring; the sol settles in storage.',
    tags: ['fining', 'cold-side', 'silicic-acid', 'vegan', 'clarity'],
    provenance: 'representative',
  },
  {
    id: 'isinglass',
    name: 'Isinglass',
    type: 'fining',
    use: 'secondary',
    form: 'liquid',
    timingMinutes: 2880,
    timingNote: 'Added to the cask or vessel and given about 48 hours to drop the yeast bright.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '10–20 mL of prepared solution per 20 L',
    dosePerLiter: null,
    description:
      'Collagen extracted from fish swim bladders, the traditional British cask fining. It forms a loose, easily disturbed sediment that lets a cask be roused and re-settled — exactly what real ale needs.',
    note: 'Heat-sensitive and short-lived: prepared solutions must be kept cold and used within weeks or they lose all activity. Not vegetarian, and it works poorly on beers with high alcohol or a stubborn powdery yeast.',
    tags: ['fining', 'cold-side', 'isinglass', 'cask', 'clarity', 'traditional'],
    provenance: 'representative',
  },
  {
    id: 'polyclar-pvpp',
    name: 'Polyclar (PVPP)',
    type: 'fining',
    use: 'secondary',
    form: 'powder',
    timingMinutes: 1440,
    timingNote: 'Stirred into cold beer and left about 24 hours, then racked off the sediment.',
    concentrationPercent: null,
    chemicalFormula: '(C₆H₉NO)ₙ',
    typicalDosage: '2–6 g per 20 L of beer',
    dosePerLiter: null,
    description:
      'An insoluble polymer that binds polyphenols specifically, so it removes the tannins responsible for chill haze and for harsh astringency. Used where a beer is clear but drinks rough, or hazes only when cold.',
    note: 'It targets polyphenols rather than yeast, so it complements a protein fining rather than replacing it. Overdosing strips color and hop character along with the tannins — start low.',
    tags: ['fining', 'cold-side', 'pvpp', 'polyphenol', 'chill-haze', 'astringency'],
    url: 'https://www.ashland.com/industries/food-and-beverage/beer-and-wine/polyclar-10-beverage-clarifier',
    provenance: 'spec-sheet',
  },
  {
    id: 'kieselsol-chitosan',
    name: 'Kieselsol and Chitosan (Two-Part Fining)',
    type: 'fining',
    use: 'secondary',
    form: 'liquid',
    timingMinutes: 2880,
    timingNote: 'Kieselsol first, chitosan after about 12 hours, then roughly 48 hours to settle.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: 'One sachet pair per 20 L of beer',
    dosePerLiter: null,
    description:
      'A two-part system: negatively charged silica sol goes in first, then positively charged chitosan, and the opposite charges sweep each other and the haze to the bottom. Clears stubborn beer and cider faster than any single fining.',
    note: 'Order and interval matter — dosing both together makes them neutralize each other in the bottle rather than in the beer. Chitosan is shellfish-derived, so it is not suitable for everyone.',
    tags: ['fining', 'cold-side', 'kieselsol', 'chitosan', 'two-part', 'clarity'],
    provenance: 'representative',
  },
  {
    id: 'bentonite',
    name: 'Bentonite',
    type: 'fining',
    use: 'primary',
    form: 'powder',
    timingMinutes: 1440,
    timingNote: 'Slurried into the fermenter and left about 24 hours before racking.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2–4 g per 20 L of beer',
    dosePerLiter: null,
    description:
      'A swelling clay with an enormous negatively charged surface that adsorbs proteins and other positively charged haze. More common in wine and cider, but effective on protein-hazy beer and entirely vegan.',
    note: 'Hydrate it in warm water and let it swell for several hours before dosing — dry powder clumps and never disperses. It makes a bulky, fluffy sediment, so expect to lose more beer to the trub than with gelatin.',
    tags: ['fining', 'clay', 'protein', 'vegan', 'clarity'],
    provenance: 'representative',
  },

  // ---------------------------------------------------------------------------
  // Nutrients — yeast nutrition and fermentation insurance
  // ---------------------------------------------------------------------------
  {
    id: 'yeast-nutrient-dap-blend',
    name: 'Yeast Nutrient (DAP Blend)',
    type: 'nutrient',
    use: 'boil',
    form: 'powder',
    timingMinutes: 10,
    timingNote: 'Added in the last 10 minutes of the boil so it is sterilized but not degraded.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1 g per 20 L of wort',
    dosePerLiter: 0.05,
    description:
      'A general-purpose blend of diammonium phosphate with yeast hulls and trace minerals, supplying the free amino nitrogen an adjunct-heavy wort lacks. Cheap insurance against a sluggish or stalled fermentation.',
    note: 'All-malt worts rarely need it; the payoff is in high-gravity beers and anything with a lot of sugar, honey or maize. Too much leaves an ammonia-like harshness, so resist doubling the dose to be safe.',
    tags: ['nutrient', 'dap', 'fan', 'high-gravity', 'fermentation'],
    provenance: 'representative',
  },
  {
    id: 'servomyces',
    name: 'Servomyces',
    type: 'nutrient',
    use: 'boil',
    form: 'tablet',
    timingMinutes: 10,
    timingNote: 'Dropped into the kettle for the final 10 minutes of the boil.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: 'One capsule per 20 L of wort',
    dosePerLiter: 0.05,
    description:
      'Yeast that has been grown to concentrate zinc in its own cell walls, then killed and dried, so the zinc arrives in a form yeast readily takes up. Sharpens attenuation and shortens lag on worts short of trace minerals.',
    note: 'Zinc is the limiting nutrient in most all-malt worts precisely because stainless kettles supply none, unlike the old copper vessels. One capsule is a full dose — this is not a nutrient to overdo.',
    tags: ['nutrient', 'zinc', 'trace-mineral', 'fermentation'],
    url: 'https://www.lallemandbrewing.com/en/global/products/servomyces/',
    provenance: 'representative',
  },
  {
    id: 'yeast-energizer',
    name: 'Yeast Energizer',
    type: 'nutrient',
    use: 'primary',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Stirred into a stuck fermentation, or into the wort at pitching.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2–4 g per 20 L of beer',
    dosePerLiter: 0.15,
    description:
      'A richer blend than plain nutrient, typically combining nitrogen salts, yeast hulls, thiamine and often a little sugar, aimed at restarting a fermentation that has stopped short. Yeast hulls also mop up the fatty acids that inhibit tired cells.',
    note: 'It cannot fix an underpitch or a cold fermenter — check temperature and cell count first, since energizer added to a genuinely healthy stall just leaves off-flavors behind.',
    tags: ['nutrient', 'energizer', 'stuck-fermentation', 'yeast-hulls'],
    provenance: 'representative',
  },
  {
    id: 'zinc-sulfate',
    name: 'Zinc Sulfate (Heptahydrate)',
    type: 'nutrient',
    use: 'boil',
    form: 'powder',
    timingMinutes: 10,
    timingNote: 'Added in the last 10 minutes of the boil.',
    concentrationPercent: null,
    chemicalFormula: 'ZnSO₄·7H₂O',
    typicalDosage: '0.02–0.05 g per 20 L of wort',
    dosePerLiter: 0.00175,
    description:
      'Straight zinc, for brewers who prefer to dose the one mineral yeast actually runs short of rather than a whole blend. Supports healthy budding and complete attenuation.',
    note: 'The target is only about 0.1–0.2 ppm zinc in the wort, which is a few hundredths of a gram in a homebrew batch — below what most kitchen scales can weigh, so make up a stock solution. Much above 0.5 ppm is toxic to yeast.',
    tags: ['nutrient', 'zinc', 'sulfate', 'trace-mineral', 'fermentation'],
    provenance: 'spec-sheet',
  },

  // ---------------------------------------------------------------------------
  // Water treatment — chlorine and chloramine removal
  // ---------------------------------------------------------------------------
  {
    id: 'campden-tablet',
    name: 'Campden Tablet (Potassium Metabisulfite)',
    type: 'water-treatment',
    use: 'water',
    form: 'tablet',
    timingMinutes: null,
    timingNote: 'Crushed into the brewing water a few minutes before mashing in.',
    concentrationPercent: null,
    chemicalFormula: 'K₂S₂O₅',
    typicalDosage: '1 tablet per 75 L of water',
    dosePerLiter: 0.0133,
    description:
      'Crushed into tap water, it destroys both chlorine and chloramine within minutes, heading off the plastic, medicinal chlorophenols they otherwise produce. The single most valuable treatment for anyone brewing on municipal water.',
    note: 'One tablet treats about 75 L, so a standard homebrew batch needs only a quarter to a third of one — dosing a whole tablet per 20 L is a common and needless overdose. Boiling removes chlorine but not chloramine; only a sulfite or carbon filter does that.',
    tags: ['water-treatment', 'chloramine', 'chlorine', 'sulfite', 'tablet'],
    provenance: 'spec-sheet',
  },
  {
    id: 'sodium-metabisulfite',
    name: 'Sodium Metabisulfite',
    type: 'water-treatment',
    use: 'water',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Dissolved into the brewing water a few minutes before mashing in.',
    concentrationPercent: null,
    chemicalFormula: 'Na₂S₂O₅',
    typicalDosage: '0.5 g per 100 L of water',
    dosePerLiter: 0.005,
    description:
      'The loose-powder equivalent of a Campden tablet, doing the same chloramine and chlorine removal at a fraction of the price per batch. Convenient when treating large volumes of liquor at once.',
    note: 'Weigh carefully — the effective dose is a fraction of a gram per batch, and the powder is far easier to overdose than a scored tablet. Sensitive drinkers react to sulfites, so the potassium version is often preferred.',
    tags: ['water-treatment', 'chloramine', 'chlorine', 'sulfite', 'sodium'],
    provenance: 'spec-sheet',
  },

  // ---------------------------------------------------------------------------
  // Chelators and antioxidants — metal binding and flavour stability
  // ---------------------------------------------------------------------------
  {
    id: 'ellagic-acid',
    name: 'Ellagic Acid (Pomegranate Extract)',
    type: 'other',
    use: 'mash',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Slurried into the strike water, or dusted onto the grist, at mash-in.',
    concentrationPercent: 90,
    chemicalFormula: 'C₁₄H₆O₈',
    typicalDosage: '0.3–1.2 g per 20 L of mash liquor (about 1.5–6 g/hL)',
    dosePerLiter: 0.0375,
    description:
      'An ellagitannin, usually sold as 90 % pomegranate extract, that chelates iron in the mash so it cannot catalyze Fenton oxidation. The most effective of the common brewing chelators at dropping wort iron and radical formation, which is why it has become a flavor-stability tool rather than a fining.',
    note: 'Dose at mash-in, not later — wort pH favours chelation and the metals need to be bound before they work through the boil. Slurry the powder in at least ten times its weight of water first; dry clumps never dissolve. It does little to copper or manganese, so it is not a complete metal fix.',
    tags: [
      'chelator',
      'ellagic-acid',
      'ellagitannin',
      'pomegranate',
      'iron',
      'antioxidant',
      'flavour-stability',
    ],
    provenance: 'representative',
  },
  {
    id: 'brewtan-b',
    name: 'Brewtan B (Gallotannin)',
    type: 'other',
    use: 'mash',
    form: 'powder',
    timingMinutes: null,
    timingNote:
      'Added at mash-in as a chelator, or in the last 10 minutes of the boil as a kettle fining.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '0.4–1.2 g per 20 L of mash or wort (2–6 ppm)',
    dosePerLiter: 0.04,
    description:
      'A brewing-grade gallotannin that both chelates iron and precipitates haze-active proline proteins. Used in the mash for flavor stability and in the kettle as a compact hot-break fining, which is why it turns up in hop-forward beers that need to last.',
    note: 'Dissolve it in warm water before dosing — the powder balls if it hits the mash dry. A little extra calcium (around 20 ppm) helps replace what the tannin binds. Overdosing strips protein the head needs, so stay inside the 2–6 ppm window and pick mash or kettle, not a stacked double dose, unless you have measured the beer will take it.',
    tags: [
      'chelator',
      'gallotannin',
      'brewtan',
      'iron',
      'kettle-fining',
      'antioxidant',
      'flavour-stability',
    ],
    provenance: 'spec-sheet',
  },
  {
    id: 'tannic-acid',
    name: 'Tannic Acid (Gallotannin)',
    type: 'other',
    use: 'mash',
    form: 'powder',
    timingMinutes: null,
    timingNote:
      'Slurried into the mash liquor at mash-in, or added late in the boil as a kettle fining.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '0.4–1.2 g per 20 L of mash or wort',
    dosePerLiter: 0.04,
    description:
      'Food-grade gallotannin, the generic stand-in for Brewtan B. Same job: bind iron in the mash and pull haze-sensitive protein into the trub. What you buy from a chemical or wine supplier is a broader mix than the brewing-purified grades, but it is the form most homebrewers can actually get.',
    note: 'Use a food-grade powder, not the technical or dye-house product. Hydrate it in warm water before it meets the mash, and treat the dose as a starting point — crude tannic acid varies more lot to lot than Brewtan, and too much leaves an astringent bite and a thin head.',
    tags: [
      'chelator',
      'gallotannin',
      'tannic-acid',
      'iron',
      'kettle-fining',
      'antioxidant',
      'flavour-stability',
    ],
    provenance: 'representative',
  },
  {
    id: 'ascorbic-acid',
    name: 'Ascorbic Acid (Vitamin C)',
    type: 'other',
    use: 'packaging',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Dissolved into a little beer and stirred in at knockout or at packaging.',
    concentrationPercent: 100,
    chemicalFormula: 'C₆H₈O₆',
    typicalDosage: '0.5–2 g per 20 L of beer',
    dosePerLiter: 0.0625,
    description:
      'A sacrificial antioxidant that scavenges dissolved oxygen and weakly chelates metals, used to slow the cardboard staling of packaged beer. Common in hop-forward beers that will sit in a keg or can, and as a partner to a mash chelator rather than a replacement for one.',
    note: 'It is not a substitute for tight oxygen control — once it is oxidized it can feed later staling reactions, so dose it into beer you have already packaged carefully. Boiling destroys it, which is why it belongs on the cold side. Keep the jar sealed; it browns in air and the brown powder is spent.',
    tags: ['antioxidant', 'ascorbic-acid', 'vitamin-c', 'oxygen', 'flavour-stability', 'packaging'],
    provenance: 'representative',
  },
  {
    id: 'sodium-erythorbate',
    name: 'Sodium Erythorbate',
    type: 'other',
    use: 'packaging',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Dissolved and added at knockout or at packaging, never boiled.',
    concentrationPercent: 100,
    chemicalFormula: 'C₆H₇NaO₆',
    typicalDosage: '0.5–2 g per 20 L of beer',
    dosePerLiter: 0.0625,
    description:
      'A more heat- and oxygen-stable isomer of ascorbic acid, widely used as a packaged-beer antioxidant. Same role — soak up the last dissolved oxygen so hop aroma and malt sweetness last — with a longer working life in the keg or can.',
    note: 'It still cannot rescue sloppy packaging, and the sodium it carries is small at brewing doses but not zero. Prefer it over ascorbic acid when the beer will sit warm or travel. Food-grade only; the industrial grade is not for beer.',
    tags: ['antioxidant', 'erythorbate', 'oxygen', 'flavour-stability', 'packaging', 'sodium'],
    provenance: 'representative',
  },

  // ---------------------------------------------------------------------------
  // Enzymes — mash conversion, haze, diacetyl and fruit processing
  // ---------------------------------------------------------------------------
  {
    id: 'clearzyme',
    name: 'Clearzyme',
    type: 'enzyme',
    use: 'primary',
    form: 'liquid',
    timingMinutes: null,
    timingNote: 'Added to the fermenter at the same time as the yeast pitch.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1/4 tsp (about 1 mL) per 20 L of wort',
    dosePerLiter: 0.05,
    description:
      'A CellarScience protease that cuts haze-active proteins so they cannot later bind hop polyphenols into chill haze. Dosed at pitching, it works through fermentation and typically leaves barley and wheat beers testing under 20 ppm gluten as a side effect.',
    note: 'It is not the same enzyme as Clarity Ferm — a broader endo- and exopeptidase from Aspergillus niger and Bacillus subtilis rather than a proline-specific protease. Store it cold, never freeze it, and recap immediately; activity fades once the bottle is open. Best between about 10–20 °C and pH 4–6.',
    tags: [
      'enzyme',
      'protease',
      'clearzyme',
      'cellarscience',
      'chill-haze',
      'clarity',
      'gluten-reduced',
    ],
    provenance: 'spec-sheet',
  },
  {
    id: 'clarity-ferm',
    name: 'Clarity Ferm (Brewers Clarex)',
    type: 'enzyme',
    use: 'primary',
    form: 'liquid',
    timingMinutes: null,
    timingNote:
      'Added to the fermenter with the yeast pitch and left to work through fermentation.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: 'One 10 mL vial per 20 L of wort',
    dosePerLiter: 0.5,
    description:
      'The White Labs proline-specific endoprotease (DSM Brewers Clarex) that hydrolyzes the exact peptide bonds chill-haze complexes form around. Prevents haze without stripping foam, and is the usual route to a gluten-reduced barley beer that still tastes like beer.',
    note: 'Add it at pitching, not later — it needs the whole fermentation to work. It does not replace a kettle fining, and gluten-reduced is not gluten-free: always test if you are brewing for coeliac drinkers. Keep the vial refrigerated.',
    tags: [
      'enzyme',
      'protease',
      'clarity-ferm',
      'brewers-clarex',
      'white-labs',
      'chill-haze',
      'clarity',
      'gluten-reduced',
    ],
    url: 'https://www.whitelabs.com/enzymes-nutrients-detail?id=8&type=ENZYMENUTRIENT',
    provenance: 'spec-sheet',
  },
  {
    id: 'aldc',
    name: 'ALDC (White Labs Brewzyme-D)',
    type: 'enzyme',
    use: 'primary',
    form: 'liquid',
    timingMinutes: null,
    timingNote: 'Dosed at yeast pitching, and again at dry hop if hop creep is a risk.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: 'One 10 mL vial per 20 L of wort',
    dosePerLiter: 0.5,
    description:
      'Converts the diacetyl precursor α-acetolactate straight to flavorless acetoin, skipping the buttery intermediate. It shortens lager rests and is now standard in heavily dry-hopped beers where hop creep would otherwise throw a late diacetyl spike. This is the White Labs homebrew vial, packed so that one vial treats a 20 L batch.',
    note: 'It prevents diacetyl forming; it cannot remove diacetyl that is already there, so it is useless as a rescue dose in finished beer. A second addition at dry hop covers hop-creep precursors. Homebrew ALDC products differ about tenfold in strength: the CellarScience dropper bottle is its own entry, and a brewery concentrate such as Maturex is dosed by the datasheet. Keep it cold and do not freeze it.',
    tags: [
      'enzyme',
      'aldc',
      'diacetyl',
      'brewzyme-d',
      'white-labs',
      'hop-creep',
      'lager',
      'dry-hop',
    ],
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-aldc',
    name: 'ALDC (CellarScience)',
    type: 'enzyme',
    use: 'primary',
    form: 'liquid',
    timingMinutes: null,
    timingNote: 'Dosed at yeast pitching, and again at dry hop if hop creep is a risk.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: 'One dropperful (about 1 mL) per 20 L of wort, or 3 mL per barrel',
    dosePerLiter: 0.045,
    description:
      'The same α-acetolactate decarboxylase as Brewzyme-D, in the concentrated dropper bottle most US homebrew shops carry: a 1 oz bottle is about thirty-five 20 L doses. Dosed at pitching it heads off diacetyl through fermentation, and a second dose at dry hop covers hop creep.',
    note: 'A dropperful is the dose, not a vial: this bottle is roughly ten times the strength of the White Labs vial, so a recipe written for Brewzyme-D will overdose it by the same margin. Overdosing wastes enzyme rather than harming the beer. Keep it cold and do not freeze it.',
    tags: ['enzyme', 'aldc', 'diacetyl', 'cellarscience', 'hop-creep', 'lager', 'dry-hop'],
    provenance: 'spec-sheet',
  },
  {
    id: 'amyloglucosidase',
    name: 'Amyloglucosidase (AMG / Ultra-Ferm)',
    type: 'enzyme',
    use: 'mash',
    form: 'liquid',
    timingMinutes: null,
    timingNote:
      'Added to the mash at a saccharification rest, or to the fermenter for extra attenuation.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1–4 mL per 20 L of mash or wort',
    dosePerLiter: 0.125,
    description:
      'A glucoamylase that clips glucose off the end of dextrins, pushing attenuation well past what malt enzymes alone can do. The enzyme behind brut IPA, low-carb beer and any beer that needs to finish bone dry, sold as Ultra-Ferm, Attenuzyme and generic AMG.',
    note: 'Mash-side it prefers about 55–60 °C and denatures in a normal mash-out; fermenter-side it keeps working and can dry a beer out from under you, so dose less and taste. It will also chew priming sugar if any is left active at packaging. Store refrigerated.',
    tags: [
      'enzyme',
      'amg',
      'glucoamylase',
      'ultra-ferm',
      'attenuzyme',
      'attenuation',
      'brut-ipa',
      'low-carb',
    ],
    provenance: 'representative',
  },
  {
    id: 'beta-glucanase',
    name: 'Beta-Glucanase',
    type: 'enzyme',
    use: 'mash',
    form: 'liquid',
    timingMinutes: null,
    timingNote: 'Added at mash-in, ideally during a short rest around 45–50 °C.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '0.5–2 mL per 20 L of mash',
    dosePerLiter: 0.0625,
    description:
      'Breaks the β-glucan gums in oats, rye, wheat and undermodified malt that otherwise turn the mash into porridge and the lauter into a stall. Standard insurance in oatmeal stout, rye IPA and any hazy that leans on flaked oats.',
    note: 'It is a mash enzyme — boiling kills it, so it does nothing in the kettle. A 10–20 minute rest near 45 °C is the usual window; adding it to a single-infusion mash still helps but less so. Do not confuse it with AMG: this thins wort, it does not dry the beer.',
    tags: ['enzyme', 'beta-glucanase', 'ultraflo', 'oats', 'rye', 'wheat', 'lauter', 'mash'],
    provenance: 'representative',
  },
  {
    id: 'pectinase',
    name: 'Pectinase',
    type: 'enzyme',
    use: 'primary',
    form: 'liquid',
    timingMinutes: 1440,
    timingNote: 'Dosed onto crushed fruit and given about 24 hours before or during fermentation.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2–5 mL per 20 L of fruit beer',
    dosePerLiter: 0.175,
    description:
      'A pectin-splitting enzyme that stops fruit haze and lets juice run from pulp, used in fruit beer, cider-adjacent beers and anything with a serious puree addition. Without it, pectin from berries, stone fruit and citrus sets a permanent haze no fining will touch.',
    note: 'Pectin sets when boiled, so treat the fruit cold or add the enzyme to the fermenter, never to the kettle. Dose on the fruit rather than the whole beer if you can. Wine-supply pectinase is the same enzyme; keep it refrigerated and use it up once opened.',
    tags: ['enzyme', 'pectinase', 'pectin', 'fruit', 'haze', 'cider'],
    provenance: 'representative',
  },
  {
    id: 'fungal-alpha-amylase',
    name: 'Fungal Alpha-Amylase',
    type: 'enzyme',
    use: 'mash',
    form: 'liquid',
    timingMinutes: null,
    timingNote: 'Added to the mash during saccharification, or in small doses to the fermenter.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1–3 mL per 20 L of mash',
    dosePerLiter: 0.1,
    description:
      'A fungal α-amylase that liquefies starch at mash temperatures lower than barley’s own enzyme prefers. Useful with raw grain, high-adjunct mashes and gluten-free grists that would otherwise leave unconverted starch, and a gentler attenuation nudge than AMG.',
    note: 'It does not take a beer to 1.000 the way AMG does — it opens starch so malt enzymes or yeast can finish the job. Optimum is typically around 50–60 °C; a mash-out will denature it. Refrigerate, and do not confuse the bottle with bacterial (thermostable) amylase, which survives the boil.',
    tags: ['enzyme', 'alpha-amylase', 'fungal', 'mash', 'adjunct', 'gluten-free', 'conversion'],
    provenance: 'representative',
  },

  // ---------------------------------------------------------------------------
  // Processing aids — foam control and microbial tools
  // ---------------------------------------------------------------------------
  {
    id: 'fermcap-s',
    name: 'Fermcap-S (Antifoam)',
    type: 'other',
    use: 'boil',
    form: 'liquid',
    timingMinutes: 60,
    timingNote:
      'A few drops in the kettle at the start of the boil, or in the fermenter at pitching.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2–4 drops per 20 L of wort',
    dosePerLiter: null,
    description:
      'A food-grade silicone antifoam that knocks kettle boil-overs and fermenter kräusen down without changing flavor. Lets you fill the kettle or cone further and is the usual answer to a hoppy wort that wants to climb out of the pot.',
    note: 'A drop is a dose — overdoing it can cost foam in the finished beer, though most of the silicone binds to the trub and is left behind. Shake the bottle; the emulsion separates. It is not a substitute for a bigger kettle or more headspace on a wild fermentation.',
    tags: ['antifoam', 'fermcap', 'silicone', 'boil-over', 'krausen', 'processing-aid'],
    provenance: 'representative',
  },
  {
    id: 'lysozyme',
    name: 'Lysozyme',
    type: 'other',
    use: 'secondary',
    form: 'powder',
    timingMinutes: null,
    timingNote:
      'Dissolved and added after a kettle sour, or to a mixed-ferm beer you want to stabilize.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '4–8 g per 20 L of beer',
    dosePerLiter: 0.3,
    description:
      'An egg-white enzyme that punches holes in gram-positive cell walls, used to stop Lactobacillus and Pediococcus after a kettle sour or to keep a mixed-fermentation beer from going more sour in the package. It leaves yeast, Brett and gram-negative bacteria alone.',
    note: 'It will not kill acetic acid bacteria, and it is useless against a Brett character you have already got. Not vegetarian, and some drinkers are egg-allergic. Add it once the souring organisms have done their work — dose too early and the sour never starts. Keep it dry and cold.',
    tags: [
      'lysozyme',
      'antimicrobial',
      'lactobacillus',
      'kettle-sour',
      'mixed-fermentation',
      'stabiliser',
    ],
    provenance: 'representative',
  },

  // ---------------------------------------------------------------------------
  // Spices and botanicals
  // ---------------------------------------------------------------------------
  {
    id: 'coriander-seed',
    name: 'Coriander Seed',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Crushed and added in the last 5 minutes of the boil, or at whirlpool.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '15–30 g per 20 L of wort',
    dosePerLiter: null,
    description:
      'The classic witbier spice, giving a lemony, faintly peppery lift that plays off wheat and orange peel. Also used sparingly in saisons and spiced winter beers.',
    note: 'Seek out the small pale Indian variety rather than the large European one — it is markedly more citrus and less soapy. Crush the seeds just before use; ground coriander from the supermarket has lost most of its aroma.',
    tags: ['spice', 'coriander', 'citrus', 'witbier', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'bitter-orange-peel',
    name: 'Bitter Orange Peel',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Added for the final 5 minutes of the boil to keep the volatile oils.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '20–40 g per 20 L of wort',
    dosePerLiter: null,
    description:
      'Dried Curaçao orange peel, the authentic partner to coriander in a Belgian white. Contributes a dry, marmalade-like bitterness and orange aroma rather than sweetness.',
    note: 'It carries genuine bitterness of its own, so heavy doses stack on top of the hops and can turn harsh. Long boiling drives off the aroma and leaves only the bitter fraction behind.',
    tags: ['spice', 'orange-peel', 'bitter', 'citrus', 'witbier', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'sweet-orange-peel',
    name: 'Sweet Orange Peel',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Added in the last 5 minutes of the boil or steeped at whirlpool.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '20–40 g per 20 L of wort',
    dosePerLiter: null,
    description:
      'Dried peel from sweet oranges, giving a rounder, jammier citrus note with far less bitterness than the Curaçao variety. Often blended with bitter peel to soften a witbier or lift a Belgian pale.',
    note: 'Fresh zest can substitute at roughly a third the weight, but take only the colored layer — the white pith is where the astringency lives.',
    tags: ['spice', 'orange-peel', 'sweet', 'citrus', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'grains-of-paradise',
    name: 'Grains of Paradise',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Crushed and added for the last 5 minutes of the boil.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '5–10 g per 20 L of wort',
    dosePerLiter: null,
    description:
      'A West African seed in the ginger family with a warm peppery bite and a hint of citrus and cardamom. Traditional in Belgian ales and a favorite for winter warmers.',
    note: 'Far more potent than it looks — the peppery heat builds in the finished beer over a few weeks, so a dose that tastes right at packaging can be overpowering by the time it is drunk.',
    tags: ['spice', 'grains-of-paradise', 'pepper', 'warming', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'vanilla-bean',
    name: 'Vanilla Bean',
    type: 'spice',
    use: 'secondary',
    form: 'solid',
    timingMinutes: 10080,
    timingNote: 'Split and steeped in the conditioning beer for about a week, tasting as it goes.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1–2 beans per 20 L of beer',
    dosePerLiter: null,
    description:
      'Split pods added on the cold side lend a creamy sweetness and round out roast and alcohol. Almost obligatory in imperial stouts and a common partner to coffee or cacao.',
    note: 'Boiling destroys the aroma, so this belongs in the fermenter. Split the pod, scrape the seeds and soak everything in a little neutral spirit first — it both sanitizes and pulls out far more flavor.',
    tags: ['spice', 'vanilla', 'sweet', 'stout', 'cold-side'],
    provenance: 'representative',
  },
  {
    id: 'cinnamon-stick',
    name: 'Cinnamon Stick',
    type: 'spice',
    use: 'secondary',
    form: 'solid',
    timingMinutes: 4320,
    timingNote: 'Steeped in the conditioning beer for about three days, tasted daily.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1–2 sticks per 20 L of beer',
    dosePerLiter: null,
    description:
      'Whole bark sticks give the warm baking-spice note that defines pumpkin ales, winter warmers and spiced holiday beers. Cold-side steeping keeps the aroma bright rather than stewed.',
    note: 'It extracts fast and dominates faster — taste every day and pull the sticks the moment it reads right. Whole sticks are far easier to control than ground cinnamon, which clouds the beer and never settles.',
    tags: ['spice', 'cinnamon', 'warming', 'winter', 'cold-side'],
    provenance: 'representative',
  },
  {
    id: 'star-anise',
    name: 'Star Anise',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Added whole for the final 5 minutes of the boil.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1–3 pods per 20 L of wort',
    dosePerLiter: null,
    description:
      'A strongly liquorice-scented pod used in tiny amounts to add depth to dark Belgian ales and spiced winter beers. One or two pods is a whole beer.',
    note: 'The most easily overdone spice on this list — a third pod can make the beer taste like aniseed sweets. Start with one, and add more at packaging only if the beer clearly wants it.',
    tags: ['spice', 'star-anise', 'liquorice', 'winter', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'fermaid-o',
    name: 'Fermaid O',
    type: 'nutrient',
    use: 'primary',
    form: 'powder',
    timingMinutes: null,
    timingNote:
      'Staggered: a third at pitch, a third at 24 hours, a third at one third sugar break.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '0.45 g/L per addition in a full TOSNA regime',
    dosePerLiter: 0.45,
    description:
      'Yeast autolysate: nitrogen the yeast digests out of dead yeast rather than out of a salt, which is why it feeds steadily instead of all at once. The organic backbone of TOSNA, and OMRI-listed, so it suits meads and ciders that want no synthetic nitrogen.',
    note: 'Organic nitrogen is roughly a fifth as concentrated as DAP by weight, so the doses look large next to a nutrient blend. That is the product, not a mistake.',
    tags: ['nutrient', 'organic', 'tosna', 'mead', 'cider', 'high-gravity'],
    provenance: 'representative',
  },
  {
    id: 'fermaid-k',
    name: 'Fermaid K',
    type: 'nutrient',
    use: 'primary',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'At pitch and again at the one third sugar break, rather than all at the start.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '0.25-0.5 g/L split across two additions',
    // Half the band's middle: 0.25–0.5 g/L is the whole fermentation's total,
    // and a recipe row is one of its two additions.
    dosePerLiter: 0.1875,
    description:
      'A complete blend: DAP for fast nitrogen, magnesium sulfate, thiamine and other vitamins, plus sterols and unsaturated fatty acids for membrane health. Covers a wort or must that is short of everything rather than short of one thing.',
    note: 'The DAP fraction makes it a poor choice for the whole regime in mead, where a late DAP hit can be thrown rather than taken up. Pair it with an organic nutrient and stop adding by the one third break.',
    tags: ['nutrient', 'dap', 'vitamin', 'sterol', 'mead', 'high-gravity'],
    provenance: 'representative',
  },
  {
    id: 'go-ferm',
    name: 'Go-Ferm',
    type: 'nutrient',
    use: 'primary',
    form: 'powder',
    timingMinutes: null,
    timingNote:
      'Stirred into the rehydration water before the dry yeast goes in, never into the wort.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1.25 g per gram of dry yeast, in 20 mL of water at 43 C',
    dosePerLiter: null,
    description:
      'A rehydration nutrient: micronutrients, sterols and unsaturated fatty acids delivered while the cell wall is still permeable, in the twenty minutes before pitching when yeast can absorb them freely. Aimed at the lag phase rather than the ferment.',
    note: 'It goes in the rehydration water, not the batch. Added to wort it is an ordinary nutrient and the whole point of the product is lost.',
    tags: ['nutrient', 'rehydration', 'sterol', 'dry-yeast', 'micronutrient'],
    provenance: 'representative',
  },
  {
    id: 'go-ferm-protect-evolution',
    name: 'Go-Ferm Protect Evolution',
    type: 'nutrient',
    use: 'primary',
    form: 'powder',
    timingMinutes: null,
    timingNote:
      'Stirred into the rehydration water before the dry yeast goes in, never into the wort.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1.25 g per gram of dry yeast, in 20 mL of water at 43 C',
    dosePerLiter: null,
    description:
      'The later Go-Ferm, reformulated to add protection against the stresses that kill a ferment late: high alcohol, high osmotic pressure and the copper and residual sulfur a fruit must can carry. Same rehydration-only use as the original.',
    note: 'Chosen over plain Go-Ferm for high-gravity musts and anything fermenting past about 14 percent, where the finish is where the trouble is.',
    tags: ['nutrient', 'rehydration', 'sterol', 'dry-yeast', 'high-gravity', 'stress'],
    provenance: 'representative',
  },
  {
    id: 'opti-white',
    name: 'Opti-WHITE',
    type: 'nutrient',
    use: 'primary',
    form: 'powder',
    timingMinutes: null,
    timingNote:
      'Added at pitch, so the glutathione is present before oxygen has anything to work on.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '0.2-0.4 g/L at pitch',
    dosePerLiter: 0.3,
    description:
      'Inactivated yeast selected for its glutathione content, which mops up oxygen radicals and protects the delicate aromatics that dry hopping and thiol work are meant to produce. Also lends a rounder mouthfeel, since the cell walls contribute mannoproteins.',
    note: 'Sold as Booster Blanc by other makers. It is a preventative and not a cure: added after a beer has already oxidized it does nothing for the flavors already lost.',
    tags: ['nutrient', 'glutathione', 'antioxidant', 'mouthfeel', 'hazy', 'thiol'],
    provenance: 'representative',
  },
  {
    id: 'american-oak-chips',
    name: 'American Oak Chips',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the oak is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'American oak runs to coconut, vanilla and sweet toast, from the lactones the species carries. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. Chips give up their flavor in days rather than months, which makes them the format to use when a beer needs oak now and the format to watch when it does not.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'american', 'chips', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'american-oak-cubes',
    name: 'American Oak Cubes',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the oak is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'American oak runs to coconut, vanilla and sweet toast, from the lactones the species carries. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. Cubes are the middle format: weeks rather than days, and far harder to overshoot than chips.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'american', 'cubes', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'american-oak-spiral',
    name: 'American Oak Spiral',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the oak is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'American oak runs to coconut, vanilla and sweet toast, from the lactones the species carries. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. A spiral is cut to expose a lot of surface at once, so it works faster than cubes for its weight and is easy to pull on a schedule.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'american', 'spiral', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'french-oak-chips',
    name: 'French Oak Chips',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the oak is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'French oak is the subtler of the two: more spice, clove and dry tannin, less coconut. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. Chips give up their flavor in days rather than months, which makes them the format to use when a beer needs oak now and the format to watch when it does not.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'french', 'chips', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'french-oak-cubes',
    name: 'French Oak Cubes',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the oak is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'French oak is the subtler of the two: more spice, clove and dry tannin, less coconut. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. Cubes are the middle format: weeks rather than days, and far harder to overshoot than chips.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'french', 'cubes', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'french-oak-spiral',
    name: 'French Oak Spiral',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the oak is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'French oak is the subtler of the two: more spice, clove and dry tannin, less coconut. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. A spiral is cut to expose a lot of surface at once, so it works faster than cubes for its weight and is easy to pull on a schedule.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'french', 'spiral', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'amburana-wood',
    name: 'Amburana Wood',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted every few days, because it moves fast.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '0.5-2 g/L, well below an oak dose',
    dosePerLiter: null,
    description:
      'A Brazilian hardwood that arrived in brewing through cachaca, giving cinnamon, tonka bean and gingerbread rather than the vanilla and toast of oak. Popular in imperial stouts, barleywines and dark Belgian beers over winter.',
    note: 'Nothing on this list is easier to overdo. At oak-like doses it stops reading as spice and starts reading as an air freshener, so start near half a gram per liter and taste often.',
    tags: ['wood', 'amburana', 'cinnamon', 'tonka', 'gingerbread', 'aging'],
    provenance: 'representative',
  },
  {
    id: 'phantasm',
    name: 'Phantasm',
    type: 'other',
    use: 'primary',
    form: 'powder',
    timingMinutes: null,
    timingNote:
      'With the dry hop, or at high krausen when the yeast still has the enzyme to work with.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '0.3-1 g/L',
    dosePerLiter: 0.65,
    description:
      'Freeze-dried Marlborough Sauvignon Blanc grape skin, ground to a powder, carrying bound 3SH and 3SHA thiol precursors. Yeast with the right enzyme cleaves them, releasing passionfruit, guava and grapefruit aromatics that the hops alone would not give.',
    note: 'It only works with a yeast that can cut the bond, so pair it with a thiol-releasing strain. On an ordinary ale yeast most of the precursor is left bound and the powder is an expensive haze.',
    tags: ['thiol', 'precursor', 'biotransformation', 'hazy', 'ipa', 'tropical'],
    provenance: 'representative',
  },
  {
    id: 'aromazyme',
    name: 'Aromazyme',
    type: 'enzyme',
    use: 'primary',
    form: 'liquid',
    timingMinutes: null,
    timingNote: 'Dosed with the dry hop and left for the duration of the contact.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '0.02-0.05 mL/L with the dry hop',
    dosePerLiter: 0.035,
    description:
      'A food-grade beta-glucosidase that cleaves the glycosidic bonds locking aroma terpenes to sugars in hop material, freeing linalool, geraniol and their relatives that would otherwise stay bound and be thrown with the trub.',
    note: 'It liberates what the hops already hold rather than adding anything, so it flatters a big dry hop and does little for a small one. Enzymes survive into package unless the beer is pasteurized, which is worth knowing before dosing a bottled beer.',
    tags: ['enzyme', 'biotransformation', 'terpene', 'dry-hop', 'aroma'],
    provenance: 'representative',
  },
  {
    id: 'potassium-sorbate',
    name: 'Potassium Sorbate',
    type: 'other',
    use: 'packaging',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'Added at packaging, with the beer already clear and the ferment finished.',
    concentrationPercent: null,
    chemicalFormula: 'C6H7KO2',
    typicalDosage: '0.2-0.4 g/L, alongside a sulfite',
    dosePerLiter: 0.3,
    description:
      'Stops yeast reproducing rather than killing it, which is what makes back-sweetening a cider, mead or fruit beer possible without a refermentation in the bottle. Standard practice is to pair it with a sulfite, which handles the cells already present.',
    note: 'Sorbate alone is not a stabilizer: without sulfite, lactic bacteria can convert sorbic acid into the geranium off-flavor, which is unmistakable and unfixable. Never use it on a beer you intend to carbonate with priming sugar.',
    tags: ['stabiliser', 'sorbate', 'back-sweetening', 'cider', 'mead', 'packaging'],
    provenance: 'representative',
  },
  {
    id: 'potassium-metabisulfite',
    name: 'Potassium Metabisulfite',
    type: 'water-treatment',
    use: 'packaging',
    form: 'powder',
    timingMinutes: null,
    timingNote: 'At packaging for stabilizing, or in the liquor a day ahead for chloramine.',
    concentrationPercent: null,
    chemicalFormula: 'K2S2O5',
    typicalDosage: '0.05-0.1 g/L for stabilizing',
    dosePerLiter: 0.075,
    description:
      'The pure salt rather than a pressed tablet, which is what makes an accurate small dose possible: a Campden tablet is a fixed 0.44 g and a fraction of one is guesswork. Used to stabilize before back-sweetening, and to strip chlorine and chloramine from brewing liquor.',
    note: 'Chosen over the sodium salt wherever the beer already carries enough sodium, since the potassium contributes nothing to flavor at these doses. Weigh it rather than measuring by spoon.',
    tags: ['stabiliser', 'sulfite', 'kmeta', 'chloramine', 'packaging', 'cider'],
    provenance: 'representative',
  },
  {
    id: 'cacao-nibs',
    name: 'Cacao Nibs',
    type: 'spice',
    use: 'secondary',
    form: 'solid',
    timingMinutes: 10080,
    timingNote: 'Steeped in the conditioning beer for about a week, tasting as it goes.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '30-80 g per 20 L',
    dosePerLiter: null,
    description:
      'Roasted cocoa beans cracked into nibs, giving the dry, bitter chocolate note that a porter or stout wants without the fat and sugar of chocolate itself. Most brewers steep them cold, often after a short soak in neutral spirit.',
    note: 'The cocoa butter they carry can thin a head, which is the usual reason a chocolate stout pours flat. Nibs from a chocolate maker are worth the premium over baking-aisle nibs.',
    tags: ['cacao', 'chocolate', 'stout', 'porter', 'adjunct'],
    provenance: 'representative',
  },
  {
    id: 'coffee-beans',
    name: 'Whole Coffee Beans',
    type: 'spice',
    use: 'secondary',
    form: 'solid',
    timingMinutes: 1440,
    timingNote: 'Cold-steeped whole in the finished beer for a day, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '20-60 g per 20 L',
    dosePerLiter: null,
    description:
      'Whole roasted beans steeped cold in finished beer, which takes the aromatics and leaves behind most of the harsh extraction a hot brew gives. Whole rather than ground keeps the beer clear and makes the contact time easy to control.',
    note: 'Steeped warm or ground fine it turns ashy and astringent within hours. A lighter roast in a blonde beer reads as fruit rather than as coffee, which surprises people.',
    tags: ['coffee', 'stout', 'porter', 'blonde', 'adjunct'],
    provenance: 'representative',
  },
  {
    id: 'juniper-berries',
    name: 'Juniper Berries',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 15,
    timingNote: 'Crushed and added for the last 15 minutes, or steeped as a tea for a sahti.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '15-40 g per 20 L',
    dosePerLiter: null,
    description:
      'The defining botanical of sahti and of the older Nordic farmhouse beers, giving resin, pine and gin-like aromatics. Traditionally the whole branch lines the lauter tun and does the filtering as well as the flavoring.',
    note: 'Crush the berries to open them. Use the culinary species and not an ornamental garden juniper, several of which are toxic.',
    tags: ['juniper', 'sahti', 'farmhouse', 'resin', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'spruce-tips',
    name: 'Spruce Tips',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 10,
    timingNote: 'Added in the last 10 minutes or at whirlpool, where the volatile oils survive.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '50-150 g per 20 L',
    dosePerLiter: null,
    description:
      'The bright new growth picked in spring, carrying citrus and resin far removed from the cough-syrup note that mature needles give. A colonial-era beer ingredient that survives in spruce ales and in modern farmhouse brewing.',
    note: 'Pick the pale new tips only, and add them late: a long boil turns them piney and harsh. Fresh and frozen both work, dried is a poor substitute.',
    tags: ['spruce', 'resin', 'citrus', 'farmhouse', 'historical'],
    provenance: 'representative',
  },
  {
    id: 'hibiscus-flowers',
    name: 'Hibiscus Flowers',
    type: 'spice',
    use: 'whirlpool',
    form: 'solid',
    timingMinutes: 10,
    timingNote: 'At whirlpool for 10 minutes, or steeped cold when the color matters most.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '30-80 g per 20 L',
    dosePerLiter: null,
    description:
      'Dried roselle calyces, which give a sharp cranberry tartness and a magenta color that arrives within minutes. A common way to make a pale sour or a saison look as tart as it tastes.',
    note: 'The color is pH-sensitive and shifts from red toward blue-gray as pH rises, so it looks best in a beer that is already acidic. Long boiling dulls both the color and the fruit.',
    tags: ['hibiscus', 'tart', 'colour', 'saison', 'sour', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'chamomile-flowers',
    name: 'Chamomile Flowers',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Added in the last 5 minutes, treated like a delicate late hop.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '15-30 g per 20 L',
    dosePerLiter: null,
    description:
      'Dried flower heads giving apple, hay and honey aromatics, used in witbiers and blonde ales where a floral lift is wanted without the citrus that coriander brings.',
    note: 'Over-steeped it turns bitter and medicinal in the way over-brewed tea does. Five minutes is generally enough.',
    tags: ['chamomile', 'floral', 'witbier', 'blonde', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'lavender',
    name: 'Lavender',
    type: 'spice',
    use: 'whirlpool',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'A short whirlpool steep, or a measured dry addition in the conditioning tank.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '5-15 g per 20 L',
    dosePerLiter: null,
    description:
      'Dried culinary lavender, giving an intensely floral, faintly minty perfume that suits saisons and pale Belgian beers in small amounts.',
    note: 'The most easily overdone botanical here after amburana: a few grams too many and the beer tastes of soap. Weigh it, and use culinary lavender rather than a craft-shop bunch.',
    tags: ['lavender', 'floral', 'saison', 'belgian', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'cardamom-pods',
    name: 'Cardamom Pods',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Cracked and added in the last 5 minutes, or steeped at whirlpool.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '5-15 g per 20 L',
    dosePerLiter: null,
    description:
      'Green pods cracked to expose the seeds, giving eucalyptus, citrus and a warm resinous spice. A winter-warmer and Belgian dubbel spice, and traditional in some Scandinavian Christmas beers.',
    note: 'Crack the pods rather than grinding them whole; the husk is largely flavorless and dilutes the dose. Black cardamom is a different and much smokier spice and is not a substitute.',
    tags: ['cardamom', 'spice', 'winter', 'dubbel', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'whole-cloves',
    name: 'Whole Cloves',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Added whole in the last 5 minutes so the oil is not driven off.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g per 20 L',
    dosePerLiter: null,
    description:
      'Whole dried flower buds carrying eugenol, the same compound a hefeweizen yeast makes for itself. Traditional in holiday warmers, spiced ales and dark Belgian beers.',
    note: 'The strongest spice by weight on this list. A handful is far too many: five or six cloves is a normal dose in 20 liters.',
    tags: ['clove', 'eugenol', 'spice', 'winter', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'nutmeg',
    name: 'Nutmeg',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Grated fresh into the last 5 minutes of the boil.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '3-8 g per 20 L',
    dosePerLiter: null,
    description:
      'Freshly grated seed giving a warm, sweet, faintly woody spice that reads as pumpkin ale and Christmas beer to most drinkers.',
    note: 'Grate it from whole seed. Pre-ground nutmeg loses its aromatics within weeks and contributes little but dust.',
    tags: ['nutmeg', 'spice', 'winter', 'pumpkin', 'warmer'],
    provenance: 'representative',
  },
  {
    id: 'allspice-berries',
    name: 'Allspice Berries',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Crushed and added in the last 5 minutes.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '5-12 g per 20 L',
    dosePerLiter: null,
    description:
      'Dried unripe berries that taste like a blend of clove, cinnamon and nutmeg, which is how they got the name. One berry does the work of three spices in a winter warmer.',
    note: 'Because it reads as several spices at once it stacks quickly with cloves and cinnamon. Count what is already in the recipe before adding it.',
    tags: ['allspice', 'pimento', 'spice', 'winter', 'warmer'],
    provenance: 'representative',
  },
  {
    id: 'ginger-root',
    name: 'Ginger Root',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 10,
    timingNote: 'Grated fresh in the last 10 minutes, or dried and added with the other spices.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '20-60 g fresh per 20 L',
    dosePerLiter: null,
    description:
      'Fresh root grated into the kettle gives a bright, hot, almost citrus lift; dried ground ginger is duller, earthier and stronger by weight. A staple of spiced ales, ginger beers and mulled winter beers.',
    note: 'Fresh and dried are not interchangeable by weight, with dried roughly six times as strong. Peel fresh root before grating, since the skin is where the bitterness sits.',
    tags: ['ginger', 'spice', 'warming', 'cider', 'winter'],
    provenance: 'representative',
  },
  {
    id: 'yarrow',
    name: 'Yarrow',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 15,
    timingNote: 'Added for the last 15 minutes, in the place a bittering hop would take.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '10-25 g per 20 L',
    dosePerLiter: null,
    description:
      'The commonest gruit herb, bitter and faintly medicinal with a sage-like aroma, used to balance beer before hops took the job. Still the starting point for anyone building a gruit.',
    note: 'Bitter without the preservative power of hops, so a gruit built on it keeps poorly and wants drinking young. Use the flowering tops.',
    tags: ['gruit', 'yarrow', 'bitter', 'historical', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'sweet-gale',
    name: 'Sweet Gale',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 15,
    timingNote: 'Added for the last 15 minutes alongside the other gruit herbs.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '10-20 g per 20 L',
    dosePerLiter: null,
    description:
      'Bog myrtle, the second of the classic gruit three, giving a resinous, bay-like, slightly peppery aroma. Common in Northern European brewing long before hops arrived.',
    note: 'Traditionally reckoned to make a beer heady rather than merely bitter, which is why it fell out of favour. Use it sparingly and avoid it entirely in beer for anyone pregnant.',
    tags: ['gruit', 'sweet-gale', 'bog-myrtle', 'resin', 'historical'],
    provenance: 'representative',
  },
  {
    id: 'wormwood',
    name: 'Wormwood',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 10,
    timingNote: 'A short late addition, or steeped as a tea and blended to taste.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-8 g per 20 L',
    dosePerLiter: null,
    description:
      'Artemisia absinthium, the bittering herb of absinthe and the third of the classic gruit botanicals. Intensely bitter with a green, camphorous aroma.',
    note: 'Extremely bitter, and unpleasant well before it is interesting: two grams in 20 liters is a real dose. Steeping a tea and blending to taste is far safer than committing it to the kettle.',
    tags: ['gruit', 'wormwood', 'absinthe', 'bitter', 'historical'],
    provenance: 'representative',
  },
  {
    id: 'heather-tips',
    name: 'Heather Tips',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 15,
    timingNote:
      'The flowering tips added for the last 15 minutes, or mashed with the grain in the old way.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '100-200 g per 20 L',
    dosePerLiter: null,
    description:
      'The flowering tops of ling heather, and the botanical behind Scottish heather ale: a dry, faintly smoky floral note with a soft bitterness of its own. Traditionally a large charge, because the flavor is gentle for the volume of plant material.',
    note: 'Pick the flowering tips in late summer and shake them out first, since the plant hosts a good deal of insect life. Woody stem contributes nothing but tannin, so strip it.',
    tags: ['gruit', 'heather', 'floral', 'scottish', 'historical', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'mugwort',
    name: 'Mugwort',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 15,
    timingNote: 'Added for the last 15 minutes, or steeped as a tea and blended to taste.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '10-25 g per 20 L',
    dosePerLiter: null,
    description:
      "Artemisia vulgaris, wormwood's milder cousin and a gruit herb in its own right: bitter, sage-like and faintly camphorous, with less of the searing bitterness that makes wormwood hard to place. Common in Northern European brewing before hops.",
    note: 'Milder than wormwood but the same family and the same caution: it carries thujone, and is not for anyone pregnant. Bitter enough that a tea blended to taste beats committing it to the kettle.',
    tags: ['gruit', 'mugwort', 'artemisia', 'bitter', 'historical', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'lemongrass',
    name: 'Lemongrass',
    type: 'spice',
    use: 'whirlpool',
    form: 'solid',
    timingMinutes: 10,
    timingNote: 'Bruised and steeped at whirlpool for 10 minutes, where the citral survives.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '30-80 g fresh per 20 L',
    dosePerLiter: null,
    description:
      'Fresh stalks bruised and steeped late, giving a clean lemon-citrus lift without the pith or rind bitterness that citrus peel brings. Suits witbiers, saisons and pale ales that want citrus from something other than the hops.',
    note: 'Bruise the stalks and use the pale lower third; the dry upper leaves are mostly grassy. Boiled hard the citral is driven off, so keep it late and low.',
    tags: ['lemongrass', 'citrus', 'witbier', 'saison', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'makrut-lime-leaves',
    name: 'Makrut Lime Leaves',
    type: 'spice',
    use: 'whirlpool',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Torn and steeped at whirlpool for 5 minutes, or dropped in the fermenter cold.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '5-15 leaves per 20 L',
    dosePerLiter: null,
    description:
      'The double leaves of the makrut lime, carrying a bright floral lime aroma quite unlike the fruit and quite unlike lemongrass. A little goes a long way in a wit, a saison or a citrus-forward pale.',
    note: 'Tear the leaves to open them and count rather than weigh, since leaf size varies wildly. Fresh or frozen both work well; the dried ones are a shadow of either.',
    tags: ['makrut', 'kaffir-lime', 'citrus', 'floral', 'witbier', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'rose-hips',
    name: 'Rose Hips',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 10,
    timingNote: 'Added in the last 10 minutes, or steeped in the conditioning vessel for a week.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '100-250 g per 20 L',
    dosePerLiter: null,
    description:
      'The fruit left after the rose flowers, giving a tart, faintly apple-and-hibiscus fruitiness and a warm amber tint. A country-wine and farmhouse ingredient that suits fruited sours and dry ciders.',
    note: 'The fine hairs around the seeds are a genuine irritant, so use split and de-seeded hips or a muslin bag and do not rack the sediment. Dried hips are stronger than fresh by weight.',
    tags: ['rose-hip', 'tart', 'fruit', 'farmhouse', 'cider', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'elderflower',
    name: 'Elderflower',
    type: 'spice',
    use: 'whirlpool',
    form: 'solid',
    timingMinutes: 5,
    timingNote:
      'A short whirlpool steep, or added cold to the conditioning tank to keep the perfume.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '20-50 g dried per 20 L',
    dosePerLiter: null,
    description:
      'The cream-colored summer umbels, giving a muscat, lychee and honeysuckle perfume that reads as sweetness without any sugar. A natural fit for saisons, wits and dry ciders.',
    note: 'The aroma is almost entirely volatile, so anything more than a brief whirlpool wastes it. Use flowers only: the green stem is bitter and mildly toxic, as is every other part of the plant raw.',
    tags: ['elderflower', 'floral', 'muscat', 'saison', 'cider', 'botanical'],
    provenance: 'representative',
  },
  {
    id: 'ancho-chilli',
    name: 'Ancho Chilli',
    type: 'spice',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Split, de-seeded and steeped in the conditioning beer, tasted daily until it is right.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-4 pods per 20 L',
    dosePerLiter: null,
    description:
      'The dried poblano, and the mild end of the chilli range: raisin, plum and mild smoke with very little heat, which is what makes it the backbone of a Mexican-style imperial stout rather than its bite.',
    note: 'De-seed and remove the pith, which is where nearly all the capsaicin sits, and toast the pods lightly first to wake the fruit up. Taste daily: chilli extraction is fast and does not stop.',
    tags: ['chilli', 'ancho', 'poblano', 'stout', 'mexican', 'adjunct'],
    provenance: 'representative',
  },
  {
    id: 'chipotle-chilli',
    name: 'Chipotle Chilli',
    type: 'spice',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote: 'Split and steeped in the conditioning beer, tasted daily and pulled early.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1-3 pods per 20 L',
    dosePerLiter: null,
    description:
      'Smoke-dried ripe jalapeno, bringing a genuine campfire smoke and a moderate, slow heat. Used where a stout or a spiced ale wants smoke and warmth from one ingredient rather than from rauchmalz and a hotter pepper.',
    note: 'The smoke arrives faster than the heat, so a beer tasted only on day one will be smokier and milder than the one you package. Buy them dried rather than in adobo, which carries tomato and vinegar with it.',
    tags: ['chilli', 'chipotle', 'smoke', 'stout', 'spiced', 'adjunct'],
    provenance: 'representative',
  },
  {
    id: 'habanero-chilli',
    name: 'Habanero Chilli',
    type: 'spice',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Steeped whole and briefly, or dosed as a tincture at packaging so the strength can be blended.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1-2 pods per 20 L, or to taste as a tincture',
    dosePerLiter: null,
    description:
      'Serious heat with a genuinely fruity, apricot-like aroma underneath it, which is the reason to use it over a hotter but blander pepper. A small presence in a big stout or a fruit beer, never a large one.',
    note: 'The one chilli on this list that is easier to ruin a batch with than to season it. Soak a pod in neutral spirit and blend the tincture into the packaged beer by the milliliter, rather than steeping and hoping.',
    tags: ['chilli', 'habanero', 'heat', 'fruit', 'stout', 'adjunct'],
    provenance: 'representative',
  },
  {
    id: 'gentian-root',
    name: 'Gentian Root',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 15,
    timingNote: 'Added for the last 15 minutes, or steeped as a tea and blended in.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-8 g per 20 L',
    dosePerLiter: null,
    description:
      'The bittering root behind most amari and a traditional gruit botanical: an intense, clean, lingering bitterness with an earthy edge and none of the resin a hop brings. Used where a botanical beer needs balance rather than aroma.',
    note: 'Astonishingly bitter by weight, and closer to a hop in strength than to a herb: two grams in 20 liters is already noticeable. Bitterness this clean also arrives late on the palate, so judge it a day after brewing rather than in the kettle.',
    tags: ['gruit', 'gentian', 'bitter', 'amaro', 'botanical', 'historical'],
    provenance: 'representative',
  },
  {
    id: 'hungarian-oak-chips',
    name: 'Hungarian Oak Chips',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the wood is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'Hungarian oak is the third traditional cooperage wood and sits between the other two: subtle vanilla like American, but with roasted hazelnut and a pronounced baking-spice lift that neither of the others gives. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. Chips give up their flavor in days rather than months, which makes them the format to use when a beer needs oak now and the format to watch when it does not.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'hungarian', 'chips', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'hungarian-oak-cubes',
    name: 'Hungarian Oak Cubes',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the wood is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'Hungarian oak is the third traditional cooperage wood and sits between the other two: subtle vanilla like American, but with roasted hazelnut and a pronounced baking-spice lift that neither of the others gives. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. Cubes are the middle format: weeks rather than days, and far harder to overshoot than chips.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'hungarian', 'cubes', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'hungarian-oak-spiral',
    name: 'Hungarian Oak Spiral',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the wood is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'Hungarian oak is the third traditional cooperage wood and sits between the other two: subtle vanilla like American, but with roasted hazelnut and a pronounced baking-spice lift that neither of the others gives. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. A spiral is cut to expose a lot of surface at once, so it works faster than cubes for its weight and is easy to pull on a schedule.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'hungarian', 'spiral', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'american-oak-staves',
    name: 'American Oak Staves',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the wood is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: "One 20-30 cm stave per 20 L, or by the maker's surface-area rating",
    dosePerLiter: null,
    description:
      'American oak runs to coconut, vanilla and sweet toast, from the lactones the species carries. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. A stave is the slow format and the one sized for a vessel rather than for a batch: months of contact, and the closest a tank, carboy or keg gets to the inside of a barrel.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'american', 'staves', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'french-oak-staves',
    name: 'French Oak Staves',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the wood is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: "One 20-30 cm stave per 20 L, or by the maker's surface-area rating",
    dosePerLiter: null,
    description:
      'French oak is the subtler of the two classics: more spice, clove and dry tannin, less coconut. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. A stave is the slow format and the one sized for a vessel rather than for a batch: months of contact, and the closest a tank, carboy or keg gets to the inside of a barrel.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'french', 'staves', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'hungarian-oak-staves',
    name: 'Hungarian Oak Staves',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the wood is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: "One 20-30 cm stave per 20 L, or by the maker's surface-area rating",
    dosePerLiter: null,
    description:
      'Hungarian oak is the third traditional cooperage wood and sits between the other two: subtle vanilla like American, but with roasted hazelnut and a pronounced baking-spice lift that neither of the others gives. Sold in light, medium, medium plus and heavy toasts, and the toast decides the character far more than the format does: light leans raw and tannic, heavy towards caramel, smoke and char. A stave is the slow format and the one sized for a vessel rather than for a batch: months of contact, and the closest a tank, carboy or keg gets to the inside of a barrel.',
    note: 'Oak is the one addition that cannot be taken back out, so taste weekly and pull it early. A beer that is barely oaked can be dosed again; one that tastes like a plank cannot be rescued.',
    tags: ['oak', 'hungarian', 'staves', 'barrel', 'aging', 'tannin'],
    provenance: 'representative',
  },
  {
    id: 'spanish-cedar',
    name: 'Spanish Cedar',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the wood is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '1-3 g/L, and tasted from the first week',
    dosePerLiter: null,
    description:
      'Not a cedar at all but Cedrela odorata, the wood cigar boxes are made from, and it tastes like one: clean sandalwood, cigar box and a dry white-pepper snap with none of the vanilla oak brings. Used in IPAs, wood-aged lagers and barleywines where the wood should read as aromatic rather than sweet.',
    note: 'Buy it as brewing or cigar-box cedar and never substitute a true cedar, cypress or thuja, which are aromatic for reasons that are not food safe. It is also faster than oak, so start at a third of an oak dose and taste early.',
    tags: ['wood', 'spanish-cedar', 'sandalwood', 'cigar-box', 'ipa', 'aging'],
    provenance: 'representative',
  },
  {
    id: 'palo-santo',
    name: 'Palo Santo',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the wood is where you want it, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '0.5-2 g/L, well below an oak dose',
    dosePerLiter: null,
    description:
      'Bursera graveolens, the South American wood that gave a well-known imperial stout its name, contributing incense and resin, a cool minty top note and a coconut-vanilla sweetness underneath. At home in imperial stouts and dark strong ales where there is enough beer to carry it.',
    note: 'Use food-grade brewing wood and never incense sticks or smudge bundles, which are often treated and are not made to go in a drink. Aromatic enough that it turns from perfume into pot-pourri quickly, so dose it like amburana rather than like oak.',
    tags: ['wood', 'palo-santo', 'incense', 'mint', 'coconut', 'stout', 'aging'],
    provenance: 'representative',
  },
  {
    id: 'bourbon-barrel-oak-cubes',
    name: 'Bourbon Barrel Oak Cubes',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted weekly until the oak and the spirit are where you want them, then removed.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'American oak charred to a genuine barrel char and then soaked in bourbon, so the wood arrives carrying both the char and the spirit. Vanilla and coconut from the oak, caramel and smoke from the char, and a whiskey warmth on top that plain heavy-toast oak cannot give. The staple route to a barrel-aged imperial stout, old ale or strong Scotch ale without a barrel.',
    note: 'Char is not toast: a charred cube leans on caramel, smoke and roast where a heavy-toast cube stays sweet. The residual spirit is real alcohol and it is not sterile — a cube that has been sitting in bourbon carries no infection risk worth worrying about, but the whiskey it releases will nudge the ABV of a small batch.',
    tags: ['oak', 'bourbon', 'whiskey', 'cubes', 'barrel', 'char', 'aging', 'vanilla', 'stout'],
    provenance: 'representative',
  },
  {
    id: 'bourbon-barrel-oak-chips',
    name: 'Bourbon Barrel Oak Chips',
    type: 'other',
    use: 'secondary',
    form: 'solid',
    timingMinutes: null,
    timingNote:
      'Added to the conditioning vessel and tasted every few days, because chips move fast.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2-6 g/L, tasted out rather than timed out',
    dosePerLiter: null,
    description:
      'The same charred, whiskey-soaked American oak as the cubes in the format that gives it all up in days rather than months. Chips are the choice when a stout needs to taste barrel-aged before the end of the month, and the format to watch hardest, because char and spirit both arrive faster than the vanilla that balances them.',
    note: 'Chips overshoot in a way cubes rarely do — start at the bottom of the range and taste every two or three days. Oak cannot be taken back out; an under-oaked beer can always be dosed again.',
    tags: ['oak', 'bourbon', 'whiskey', 'chips', 'barrel', 'char', 'aging', 'stout'],
    provenance: 'representative',
  },
  {
    id: 'lemon-peel',
    name: 'Lemon Peel',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Added in the last 5 minutes of the boil or steeped at whirlpool.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '15-30 g per 20 L of wort',
    dosePerLiter: null,
    description:
      'Dried lemon zest, sharper and higher-toned than either orange peel and with almost none of their marmalade sweetness. It lifts a witbier or a saison towards lemonade, and sits well under the citrus hops in a hazy pale ale where a hop charge alone reads flat.',
    note: 'Fresh zest can substitute at roughly three times the weight of dried, and in both cases take only the colored layer — the white pith carries the astringency. Lemon oil fades faster in the package than orange does, so a beer built on it is best drunk young.',
    tags: ['spice', 'lemon-peel', 'citrus', 'zest', 'witbier', 'saison', 'hazy'],
    provenance: 'representative',
  },
  {
    id: 'lime-peel',
    name: 'Lime Peel',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Added in the last 5 minutes of the boil or steeped at whirlpool.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '10-25 g per 20 L of wort',
    dosePerLiter: null,
    description:
      'Dried lime zest, greener and more resinous than lemon, with a faint bitterness behind the aroma. It is the peel for a gose or a Mexican-style lager, and it pairs with the tropical hops in a hazy pale ale more naturally than orange does.',
    note: 'More assertive than lemon at the same weight, so dose it lower. Distinct from makrut lime leaves, which are a different plant part and a different aroma altogether.',
    tags: ['spice', 'lime-peel', 'citrus', 'zest', 'gose', 'lager', 'hazy'],
    provenance: 'representative',
  },
  {
    id: 'grapefruit-peel',
    name: 'Grapefruit Peel',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Added in the last 5 minutes of the boil or steeped at whirlpool.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '20-40 g per 20 L of wort',
    dosePerLiter: null,
    description:
      'Dried grapefruit zest, the pithiest of the citrus peels and the one that reads closest to hops. It reinforces the grapefruit note in Cascade, Centennial and Simcoe rather than adding a spice character of its own, which is why it turns up in IPAs more often than in Belgian beers.',
    note: 'It carries genuine bitterness alongside the aroma, so it stacks on top of the hops the way bitter orange peel does. In a beer already at 60 IBU that is a bigger contribution than it looks.',
    tags: ['spice', 'grapefruit-peel', 'citrus', 'zest', 'bitter', 'ipa', 'hazy'],
    provenance: 'representative',
  },
  {
    id: 'licorice-root',
    name: 'Licorice Root',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 15,
    timingNote: 'Added for the last 15 minutes of the boil to draw the flavor out of the root.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '5-15 g per 20 L of wort',
    dosePerLiter: null,
    description:
      'Glycyrrhiza glabra, the historical botanical of foreign extra stout, Baltic porter and the English winter warmer. It reads as sweetness without adding sugar, rounds the edge off roast malt, and leaves a body and a mouthfeel that the gravity alone does not explain.',
    note: 'Use the root, cut or shredded, rather than confectionery licorice or the black sticks sold as sweets — those are mostly sugar, anise and molasses and will do something else entirely. Glycyrrhizin is a genuine foam stabilizer at these doses, which is a bonus in a stout and a nuisance in a fermenter with no headspace.',
    tags: [
      'spice',
      'licorice',
      'botanical',
      'root',
      'stout',
      'porter',
      'winter-warmer',
      'historical',
    ],
    provenance: 'representative',
  },
  {
    id: 'black-peppercorns',
    name: 'Black Peppercorns',
    type: 'spice',
    use: 'boil',
    form: 'solid',
    timingMinutes: 5,
    timingNote: 'Cracked and added for the last 5 minutes of the boil, or steeped at whirlpool.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '3-8 g per 20 L of wort',
    dosePerLiter: null,
    description:
      'Piper nigrum, cracked rather than ground, giving the peppery bite that a saison yeast only suggests. It is the usual way to push a farmhouse ale or a spiced Belgian further in the direction its phenols already point, and it does more work in a dry beer than a sweet one. Tellicherry is the same berry left to ripen longer and graded larger, and it is winier and more floral for it.',
    note: 'Crack it coarsely and add it late: ground pepper goes dusty and harsh, and a long boil drives off the aroma and leaves only the heat. Like grains of paradise, the bite builds in the package over a few weeks, so a dose that tastes right at bottling can be too much by the time it is drunk.',
    tags: [
      'spice',
      'pepper',
      'peppercorn',
      'black-pepper',
      'tellicherry',
      'saison',
      'farmhouse',
      'belgian',
    ],
    provenance: 'representative',
  },
  {
    id: 'pink-peppercorns',
    name: 'Pink Peppercorns',
    type: 'spice',
    use: 'whirlpool',
    form: 'solid',
    timingMinutes: 10,
    timingNote: 'Cracked and steeped at whirlpool for about 10 minutes, below boiling.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '3-10 g per 20 L of wort',
    dosePerLiter: null,
    description:
      'Not a pepper at all but the dried berry of a Peruvian pepper tree, and it behaves nothing like Piper nigrum: sweet, resinous and faintly floral, with a rose and juniper edge and only a trace of heat. It suits pale saisons, witbiers and gin-adjacent beers where black pepper would be too coarse.',
    note: 'The aroma is fragile and mostly boils away, so treat it as a whirlpool or secondary addition rather than a boil spice. It is related to cashew and mango, so it is worth naming on the label for anyone with a tree-nut allergy.',
    tags: ['spice', 'pink-peppercorn', 'floral', 'resinous', 'saison', 'witbier', 'botanical'],
    provenance: 'representative',
  },

  // ---------------------------------------------------------------------------
  // Hop flavour — terpene blends and aroma concentrates, dosed by volume
  // ---------------------------------------------------------------------------
  {
    id: 'abstrax-hop-profiles',
    name: 'Abstrax Hop Profiles',
    type: 'hop-flavour',
    use: 'packaging',
    form: 'liquid',
    timingMinutes: null,
    timingNote: 'Dosed into the keg or fermenter after fermentation; some brewers dose the glass.',
    concentrationPercent: null,
    chemicalFormula: null,
    typicalDosage: '2–3 mL per 19 L (5 gal), to taste',
    dosePerLiter: 0.13,
    description:
      'Terpene extracts sold by flavor profile rather than by variety, dosed at about 2.5 mL per 5-gallon batch anywhere on the cold side — fermenter, keg, even the finished glass. Worth knowing what it is: the profiles are assembled from individual terpene isolates to match a hop, rather than pressed out of one, so this is hop-flavored rather than hop-derived. No alpha acid and no bitterness.',
    note: null,
    tags: ['hop-flavour', 'terpene', 'aroma', 'abstrax', 'product'],
    provenance: 'representative',
  },
];
